<script lang="ts">
  // Physician demo — console.health.dreamgiga.ai/demo
  // Access key → /api/demo/open → synthetic 12-year record → condition-centred
  // dashboard. No sign-in: the key (issued in the admin console) is the credential.
  import { openDemo, savedSession, clearSession, keyFromUrl, type DemoSession } from './api.ts';
  import { collate, byUrgency, fromDataset, type ConditionTrack } from './tracking/collate.ts';
  import { fmtDate } from './format.ts';
  import Overview from './views/Overview.svelte';
  import Conditions from './views/Conditions.svelte';
  import Labs from './views/Labs.svelte';
  import VitalsView from './views/Vitals.svelte';
  import Medications from './views/Medications.svelte';
  import Notes from './views/Notes.svelte';

  type Tab = 'overview' | 'conditions' | 'labs' | 'vitals' | 'medications' | 'notes';
  const TABS: { id: Tab; label: string }[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'conditions', label: 'Conditions & care history' },
    { id: 'labs', label: 'Labs' },
    { id: 'vitals', label: 'Vitals' },
    { id: 'medications', label: 'Medications' },
    { id: 'notes', label: 'Visit notes' },
  ];

  let session = $state<DemoSession | null>(savedSession());
  let key = $state(keyFromUrl());
  let busy = $state(false);
  let err = $state('');

  // Route = location.hash:  #overview · #conditions/<id> · #labs …
  function parseHash(): { tab: Tab; cond: string } {
    const h = location.hash.replace(/^#/, '');
    const [t, c] = h.split('/');
    const tab = (TABS.some((x) => x.id === t) ? t : 'overview') as Tab;
    return { tab, cond: c || '' };
  }
  let route = $state(parseHash());
  $effect(() => {
    const on = () => (route = parseHash());
    window.addEventListener('hashchange', on);
    return () => window.removeEventListener('hashchange', on);
  });
  const go = (tab: Tab, cond = '') => { location.hash = cond ? `${tab}/${cond}` : tab; };

  const tracks = $derived<ConditionTrack[]>(session ? byUrgency(collate(fromDataset(session.dataset))) : []);
  const selectedCond = $derived(route.cond || (tracks[0]?.spec.id ?? ''));

  async function open() {
    busy = true; err = '';
    try {
      session = await openDemo(key);
      try { history.replaceState(null, '', location.pathname + (location.hash || '#overview')); } catch { /* ignore */ }
    } catch (e) { err = (e as Error)?.message || String(e); }
    finally { busy = false; }
  }
  function end() { clearSession(); session = null; key = ''; location.hash = ''; }
  // Auto-open when a key arrives in the URL (one-click link from the admin console).
  $effect(() => { if (!session && key && !busy && !err) void open(); });
</script>

{#if !session}
  <div class="gate">
    <div class="hero" style="background-image: url(/hero-login.jpg)" aria-hidden="true"><div class="scrim"></div></div>
    <div class="panel">
      <form class="card" onsubmit={(e) => { e.preventDefault(); void open(); }}>
        <div class="brandrow"><img class="lotus" src="/wellness-blue.svg" alt="" aria-hidden="true" /><span class="brand">empowered</span><span class="chip">Physician demo</span></div>
        <h1>Enter your access key</h1>
        <p class="sub">Physician tracking view: a 12-year record of one patient with type 2 diabetes — every clinic visit, lab panel, medication change and vital sign — collated under the condition it belongs to. The same key opens the record in the patient app at <code>/demo</code>.</p>
        <label class="lbl" for="key">Access key</label>
        <input id="key" class="in" type="text" autocomplete="off" spellcheck="false" placeholder="XXXX-XXXX-XXXX" bind:value={key} disabled={busy} />
        <button class="go" type="submit" disabled={busy || key.replace(/[^a-z0-9]/gi, '').length < 8}>{busy ? 'Opening…' : 'Open the demo record'}</button>
        {#if err}<p class="err">{err}</p>{/if}
        <p class="legal">Synthetic data: this patient does not exist. Keys are issued and can be revoked by the administrator.</p>
      </form>
    </div>
  </div>
{:else}
  {@const ds = session.dataset}
  <div class="app">
    <header class="top">
      <div class="brandrow"><img class="lotus" src="/wellness-blue.svg" alt="" aria-hidden="true" /><span class="brand">empowered</span><span class="chip">Physician demo</span></div>
      <div class="pt">
        <span class="name">{ds.patient.name}</span>
        <span class="meta t-foot">{ds.patient.ageYears} y · {ds.patient.sex} · MRN {ds.patient.mrn} · {ds.patient.diagnosis.condition} since {fmtDate(ds.patient.diagnosis.date)} · last visit {fmtDate(ds.summary.latestVisit)}</span>
      </div>
      <div class="right">
        <a class="applink" href={`/demo/?key=${encodeURIComponent(session.access.key)}`} title="Open the same record in the patient app">Open in the app</a>
        <span class="synth" title={ds.meta.disclaimer}>Synthetic record</span>
        <span class="who t-foot" title="Access key label">{session.access.label}</span>
        <button class="end" onclick={end}>End session</button>
      </div>
    </header>
    <nav class="tabs" aria-label="Sections">
      {#each TABS as t (t.id)}
        <button class:on={route.tab === t.id} aria-current={route.tab === t.id ? 'page' : undefined} onclick={() => go(t.id)}>{t.label}</button>
      {/each}
    </nav>
    <main class="main">
      {#if route.tab === 'overview'}<Overview {ds} {tracks} onOpen={(id) => go('conditions', id)} />
      {:else if route.tab === 'conditions'}<Conditions {ds} {tracks} selectedId={selectedCond} onSelect={(id) => go('conditions', id)} />
      {:else if route.tab === 'labs'}<Labs {ds} onOpen={(id) => go('conditions', id)} />
      {:else if route.tab === 'vitals'}<VitalsView {ds} onOpen={(id) => go('conditions', id)} />
      {:else if route.tab === 'medications'}<Medications {ds} {tracks} />
      {:else}<Notes {ds} />{/if}
    </main>
    <footer class="foot t-foot">{ds.meta.disclaimer} Dataset {ds.meta.id} v{ds.meta.version} · {ds.meta.cadence}</footer>
  </div>
{/if}

<style>
  /* ── gate ── */
  .gate { min-height: 100dvh; display: grid; grid-template-rows: 32vh 1fr; }
  @media (min-width: 900px) { .gate { grid-template-columns: 1fr 1fr; grid-template-rows: 1fr; } }
  .hero { position: relative; background-size: cover; background-position: center 35%; }
  .scrim { position: absolute; inset: 0; background: linear-gradient(180deg, rgba(245, 247, 250, 0.1), rgba(245, 247, 250, 0.55)); }
  .panel { display: grid; place-items: center; padding: var(--space-6); }
  .card { width: min(100%, 460px); display: flex; flex-direction: column; gap: var(--space-3); background: var(--surface); border: 1px solid var(--line); border-radius: var(--radius-lg); padding: var(--space-8); }
  .brandrow { display: flex; align-items: center; gap: var(--space-2); }
  .lotus { width: 24px; height: 24px; }
  .brand { font-family: 'Audiowide', system-ui, sans-serif; color: var(--accent); font-size: var(--fs-heading); }
  .chip { font-size: var(--fs-foot); font-weight: 800; letter-spacing: 0.12em; text-transform: uppercase; color: var(--text-3); }
  h1 { margin-top: var(--space-2); }
  .sub { color: var(--text-2); }
  .lbl { font-size: var(--fs-foot); font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; color: var(--text-3); margin-top: var(--space-2); }
  .in { padding: var(--space-3) var(--space-4); border: 1px solid var(--line-strong); border-radius: var(--radius-md); font-family: var(--mono); font-size: var(--fs-heading); letter-spacing: 0.12em; text-transform: uppercase; background: var(--surface); }
  .in:focus { outline: 2px solid var(--accent); outline-offset: 1px; }
  .go { padding: var(--space-3) var(--space-5); border-radius: var(--radius-pill); background: var(--accent-grad); color: var(--on-accent); font-weight: 700; }
  .go:disabled { opacity: 0.55; cursor: default; }
  .err { color: var(--danger); font-size: var(--fs-body); }
  .legal { color: var(--text-3); font-size: var(--fs-foot); line-height: 1.5; }

  /* ── app ── */
  .app { min-height: 100dvh; display: flex; flex-direction: column; }
  .top { display: flex; align-items: center; gap: var(--space-5); padding: var(--space-3) var(--space-6); background: var(--surface); border-bottom: 1px solid var(--line); flex-wrap: wrap; }
  .pt { display: flex; flex-direction: column; min-width: 0; flex: 1; }
  .name { font-size: var(--fs-heading); font-weight: var(--fw-heading); }
  .meta { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .right { display: flex; align-items: center; gap: var(--space-3); }
  .applink { font-size: var(--fs-foot); font-weight: 700; color: var(--accent); border: 1px solid var(--accent); border-radius: var(--radius-pill); padding: 2px 10px; }
  .applink:hover { background: var(--accent-soft); }
  .synth { font-size: var(--fs-foot); font-weight: 800; letter-spacing: 0.1em; text-transform: uppercase; color: var(--warn); background: var(--warn-soft); border: 1px solid color-mix(in srgb, var(--warn) 40%, transparent); border-radius: var(--radius-pill); padding: 2px 8px; cursor: help; }
  .who { max-width: 180px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .end { padding: var(--space-1) var(--space-3); border-radius: var(--radius-pill); border: 1px solid var(--line-strong); color: var(--text-2); font-weight: 700; font-size: var(--fs-foot); background: var(--surface-2); }
  .end:hover { color: var(--danger); border-color: var(--danger); }
  .tabs { display: flex; gap: 2px; padding: 0 var(--space-6); background: var(--surface); border-bottom: 1px solid var(--line); overflow-x: auto; }
  .tabs button { padding: var(--space-3) var(--space-4); color: var(--text-2); font-weight: 600; white-space: nowrap; border-bottom: 2px solid transparent; margin-bottom: -1px; }
  .tabs button:hover { color: var(--text); }
  .tabs button.on { color: var(--accent); border-bottom-color: var(--accent); }
  .main { flex: 1; padding: var(--space-6); max-width: 1400px; width: 100%; margin: 0 auto; }
  .foot { padding: var(--space-4) var(--space-6); border-top: 1px solid var(--line); line-height: 1.5; }
  @media (max-width: 700px) { .top, .tabs, .main, .foot { padding-left: 16px; padding-right: 16px; } .main { padding-top: var(--space-4); } }
</style>
